import { useEffect } from "react";
import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { scrollToElement } from "../hooks/useRoute";

const serviceCategories = [
  {
    id: "it-ites",
    title: "IT & ITeS Services",
    badge: "Software & Infrastructure",
    description: "End-to-end software development, technical consultancy, systems integration, and resilient networking infrastructure designed for high operational stability.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25h-13.5A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25h-13.5A2.25 2.25 0 013 12V5.25" />
      </svg>
    ),
    capabilities: [
      "Custom Web & Enterprise Application Development",
      "Systems Architecture & IT Technical Consultancy",
      "API Development, Microservices & Data Integration",
      "Network Infrastructure & Communication Setup",
      "System Diagnostics & Annual Maintenance Contracts (AMC)"
    ],
  },
  {
    id: "saas-platforms",
    title: "SaaS & Digital Platforms",
    badge: "Cloud & Product Engineering",
    description: "Design, architecture, licensing, and scaling of software-as-a-service (SaaS) products, dynamic CMS websites, and custom digital platforms, including our flagship RaBuOS business operating platform (Public Beta Q4 2026).",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125v-3.75" />
      </svg>
    ),
    capabilities: [
      "Multi-tenant SaaS Architecture & Platform Engineering",
      "CMS-Driven Content & E-Commerce Web Applications",
      "Secure Payment Gateway & User Account Systems",
      "CI/CD Pipeline Setup & Cloud Managed Hosting",
      "Performance Optimisation, Caching & Analytics"
    ],
  },
  {
    id: "rnd-hardware",
    title: "Research & Development",
    badge: "Prototyping & Applied Engineering",
    description: "Applied engineering research, electronic hardware prototyping, Internet of Things (IoT) sensor architectures, and technical infrastructure support for specialized hardware and software integration.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.032 14.47A4.5 4.5 0 008.212 22h7.576a4.5 4.5 0 003.18-7.53l-4.06-4.061a2.25 2.25 0 01-.658-1.591V3.104M9.75 3.104c-.251.037-.502.082-.75.137M9.75 3.104h4.5m0 0c.25.037.5.082.75.137M12 15.75h.008v.008H12v-.008z" />
      </svg>
    ),
    capabilities: [
      "Applied Electronic & Computing System Prototyping",
      "Internet of Things (IoT) & Industrial IoT (IIoT) Solutions",
      "Embedded Microcontroller & Circuit Board Design",
      "Custom Firmware Development & Hardware Debugging",
      "Sensor Telemetry & Edge Computing Integration"
    ],
  },
  {
    id: "robotics-automation",
    title: "Robotics & Automation",
    badge: "Intelligent Systems",
    description: "Design, assembly, control logic programming, and maintenance of electronic, electrical, and robotic systems with real-time IoT telemetry for commercial and industrial automation.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
    capabilities: [
      "Industrial & Commercial Process Automation",
      "IoT-Connected Motion Control & Automated Robotics",
      "PLC, Microcontroller & SCADA Interface Integration",
      "Real-Time Telemetry, Edge Analytics & Environmental Monitoring",
      "System Testing, Calibration & Preventative Maintenance"
    ],
  },
  {
    id: "security-systems",
    title: "Security & Surveillance Systems",
    badge: "Infrastructure Protection",
    description: "Deployment of physical and digital security infrastructure, smart IoT surveillance systems, access controls, and technical safeguards for commercial and industrial premises.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" />
      </svg>
    ),
    capabilities: [
      "Smart IP Video Surveillance & CCTV Network Architecture",
      "IoT-Integrated Biometric Access Control & Gate Automation",
      "Perimeter Intrusion Detection & Connected Alarm Systems",
      "Network Firewall & Operational System Hardening",
      "Maintenance & On-Site Emergency Security Support"
    ],
  },
  {
    id: "training-education",
    title: "Technology Training & Education",
    badge: "Knowledge & Skill Building",
    description: "Structured corporate technology training, enterprise developer upskilling programs, professional engineering tracks, and lab setup consultancy for institutions.",
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-5.25 6.557c0 1.5 1.5 3 5.25 3s5.25-1.5 5.25-3m-10.5 0h10.5" />
      </svg>
    ),
    capabilities: [
      "Enterprise Technology & Systems Engineering Programs",
      "Corporate Upskilling & Developer Onboarding Programs",
      "Embedded Systems, Robotics & Internet of Things (IoT) Tracks",
      "Curriculum Design & Educational Lab Setup Consultancy",
      "Technical Architecture & Professional Development Seminars"
    ],
  },
];

const deliveryProcess = [
  {
    step: "01",
    title: "Discovery & Requirements",
    desc: "We analyze your business context, operational constraints, and technical scope to define clear goals and specifications.",
  },
  {
    step: "02",
    title: "Architecture & Prototyping",
    desc: "Our engineering team designs robust system architectures and develops prototypes for early validation.",
  },
  {
    step: "03",
    title: "Implementation & Testing",
    desc: "We build using clean code standards, component isolation, and rigorous quality assurance before deployment.",
  },
  {
    step: "04",
    title: "Deployment & Managed Support",
    desc: "We handle seamless rollout, documentation, and ongoing Annual Maintenance Contracts (AMC) to ensure long-term stability.",
  },
];

export function Services({ scrollTo }: { scrollTo?: string }) {
  useEffect(() => {
    if (scrollTo) {
      scrollToElement(scrollTo);
    }
  }, [scrollTo]);

  return (
    <>
      <PageHero
        badge="RADIECE Capabilities"
        title="Our Services & Solutions"
        subtitle="Engineering & Technology Capabilities"
        description="Delivering disciplined engineering, custom software development, intelligent hardware prototyping, security systems, and technical education."
      >
        {serviceCategories.map((c) => (
          <motion.a
            key={c.id}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.96 }}
            href={`#${c.id}`}
            className="bg-white/10 border border-white/20 rounded-full px-4 py-1.5 font-medium hover:border-primary/50 hover:bg-white/20 transition-colors"
          >
            {c.title}
          </motion.a>
        ))}
      </PageHero>

      {/* Services Grid Section */}
      <section className="no-print bg-white dark:bg-[#0b131e] py-16 sm:py-20 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Core Practice Areas</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Comprehensive technical solutions for modern operations.
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              We structure our engineering and software capabilities across six core verticals, providing targeted solutions or integrated multi-disciplinary projects.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCategories.map((cat, idx) => (
              <motion.div
                key={cat.id}
                id={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: idx * 0.06, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="card-interactive scroll-mt-28 bg-slate-50 dark:bg-[#0e1724] border border-slate-200/80 dark:border-slate-800 rounded-2xl p-8 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
              >
                <div>
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="w-12 h-12 bg-brand/10 dark:bg-primary/15 text-brand dark:text-primary rounded-xl flex items-center justify-center shrink-0 border border-brand/20 dark:border-primary/30">
                      {cat.icon}
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-3 py-1">
                      {cat.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-brand-dark dark:text-slate-100 mb-3">{cat.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">{cat.description}</p>

                  <div className="border-t border-slate-200/80 dark:border-slate-800 pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-primary mb-3">Key Deliverables</p>
                    <ul className="space-y-2.5">
                      {cat.capabilities.map((cap) => (
                        <li key={cap} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                          <svg className="w-4 h-4 mt-0.5 text-primary shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-200/60 dark:border-slate-800 flex flex-col gap-2.5">
                  <div className="flex items-center justify-between">
                    <a
                      href="/pricing"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand dark:text-primary hover:text-brand-dark dark:hover:text-sky-300 transition-colors"
                    >
                      View Pricing & Models
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </a>
                  </div>
                  {cat.id === "training-education" && (
                    <a
                      href="/courses"
                      className="btn-shine inline-flex items-center justify-center gap-1.5 w-full py-2.5 px-4 bg-brand dark:bg-primary text-white dark:text-slate-950 hover:bg-brand-dark dark:hover:bg-sky-400 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
                    >
                      Explore Courses Syllabus →
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Process Section */}
      <section className="no-print bg-slate-50 dark:bg-[#070b12] py-16 sm:py-20 border-t border-b border-slate-100 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-3xl mx-auto mb-14"
          >
            <p className="text-brand dark:text-primary font-semibold text-sm uppercase tracking-wider mb-2">Our Workflow</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Disciplined service delivery from concept to maintenance.
            </h2>
            <p className="mt-3 text-base text-slate-600 dark:text-slate-300 leading-relaxed">
              Every project follows a structured engineering workflow designed for transparency, technical quality, and predictable outcomes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {deliveryProcess.map((p, idx) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white dark:bg-[#0e1724] rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800 relative transition-colors"
              >
                <span className="text-3xl font-extrabold text-brand/20 dark:text-primary/30 font-mono block mb-2">{p.step}</span>
                <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Navigation CTA */}
      <section className="no-print bg-slate-900 py-16 sm:py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
            Discuss Your Technical Service Requirement
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base sm:text-lg mb-8 leading-relaxed">
            Need a tailored proposal or detailed technical scope? Our practice leads are available to assist you.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-8 py-3.5 rounded-xl font-bold text-base transition-all duration-200 shadow-lg"
          >
            Contact Practice Engineers →
          </a>
        </div>
      </section>
    </>
  );
}
